import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FormSuccess } from 'Components/Form/Success';
import { Container } from 'Components/Container';
import { Column, Row } from 'Components/Grid';
import { ButtonForm, CustomTextField, TitleForm } from 'Components/Form/styles';
import { addUser, editUser, getUserById } from 'services/users-service';
import { Box, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, useTheme } from '@mui/material';


import { getUsersGroups } from 'services/users-groups-service';
import { useQuery } from 'react-query';
import { getCompanyRolesBySlug } from 'services/company-service';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { getSelectedContentByCompareFields } from 'utils/select_utils';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useCountryConfig } from 'hooks/useCountryConfig';

import { PageLoaderForm } from 'Components/PageLoader/Form';
import { getStyles, MenuProps } from './actions';
import { PageHelper } from '../../../Components/PageHelper';
import { useTranslation } from 'react-i18next';

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao formulário de Usuários! Aqui você pode adicionar ou editar usuários do sistema.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique aqui no título para voltar à lista de Usuários.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="user-name"]',
    content: 'Digite o nome completo do usuário.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="user-email"]',
    content: 'Informe o email do usuário. Este email será usado para login no sistema.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="user-phone"]',
    content: 'Opcionalmente, informe o telefone de contato do usuário.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="user-groups"]',
    content: 'Selecione um ou mais grupos aos quais o usuário pertence. Grupos facilitam o envio de políticas e gerenciamento de permissões.',
  },
  {
    target: '[data-tour="user-permissions"]',
    content: 'Selecione a permissão (perfil) do usuário. Isso define o nível de acesso dele no sistema.',
  },
  {
    target: '[data-tour="form-actions"]',
    content: 'Use "Cancelar" para descartar as alterações ou "Salvar" para criar/atualizar o usuário.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Preencha as informações e salve o usuário.',
    placement: 'center',
  },
];

export const UsersForm = () => {
  const { t } = useTranslation(['user', 'common']);
  const theme = useTheme();
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const effectivePhoneCountry = phoneCountry ?? selectedTenant?.country;
  const { phonePrefix, allConfigs } = useCountryConfig(effectivePhoneCountry);
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [idUser, setIdUser] = useState(null);
  const [finished, setFinished] = useState(false);

  const { data: userGroups } = useQuery(['getUserGroups'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersGroups(selectedTenant.slug);
    return !!result?.data && result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyRoles } = useQuery(['getCompanyRoles'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getCompanyRolesBySlug(selectedTenant.slug);
    return (!!result?.data && result?.data.length) > 0 ? result?.data : [];
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      active: true,
      name: '',
      email: '',
      cellphone: '',
      groups: [],
      permissions: [],
    },
  });

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdUser(null);
    navigate('/users/add');
  };

  const {
    data: user,
    isLoading,
    error,
  } = useQuery(['getUser', id], async () => {
    if (!id) return null;

    const result = await getUserById(id);

    if (!result?.data) return null;

    return result?.data;
  });

  const formatUser = values => {
    // Garante que permissions seja sempre um array, mesmo quando o Select retorna um objeto único
    const permissionsArray = Array.isArray(values.permissions)
      ? values.permissions
      : values.permissions
      ? [values.permissions]
      : [];

    if (id) {
      const groupsUser =
        !!user.groups && user.groups.length > 0
          ? user.groups?.filter(group => group.companyKey !== selectedTenant.slug)
          : [];
      return {
        name: values.name,
        cellphone: values.cellphone,
        email: values.email,
        groups: [
          ...groupsUser,
          ...values.groups.map(group => {
            return {
              companyKey: selectedTenant.slug,
              name: group.name,
            };
          }),
        ],
        permissions: [
          ...user.permissions.filter(permission => permission.companyKey !== selectedTenant.slug),
          ...permissionsArray.map(permission => ({
            companyKey: selectedTenant.slug,
            roleKey: permission.roleKey,
          })),
        ],
        active: user.active,
      };
    }
    return {
      name: values.name,
      cellphone: values.cellphone,
      email: values.email,
      active: true,
      groups: values.groups.map(group => {
        return {
          companyKey: selectedTenant.slug,
          name: group.name,
        };
      }),
      permissions: permissionsArray.map(permission => ({
        companyKey: selectedTenant.slug,
        roleKey: permission.roleKey,
      })),
    };
  };

  const onSubmit = async () => {
    try {
      const res = id
        ? await editUser(id, selectedTenant.slug, formatUser(getValues()))
        : await addUser(formatUser(getValues()));
      if (res.status === 200 || res.status === 201) {
        setIdUser(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar/editar o Usuário!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        html: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  useEffect(() => {
    if (!id) return;
    if (!user) return;
    if (!userGroups) return;

    Object.keys(user).forEach(key => {
      if (key === 'permissions') {
        // Filtrar permissões da empresa atual e converter para array
        const userPermissions = user[key].filter(item => item.companyKey === selectedTenant.slug);
        setValue(key, userPermissions);
      } else if (key === 'groups') {
        // Filtra apenas grupos da empresa atual e busca os objetos completos de userGroups
        const userGroupsFiltered = user[key]?.filter(g => g.companyKey === selectedTenant.slug) ?? [];
        const fullGroups = userGroupsFiltered
          .map(userGroup => {
            return userGroups?.find(ug => ug.name === userGroup.name) || null;
          })
          .filter(g => g !== null && g._id); // Remove grupos que não foram encontrados
        setValue(key, fullGroups);
      } else {
        setValue(key, user[key]);
      }
    });

    return () => {};
  }, [id, user, selectedTenant, setValue, userGroups]);

  const showForm = () => {
    if (id) return !isLoading && !error;
    return true;
  };

  return (
    <>
      {!finished && (
        <PageHelper 
          steps={tourSteps}
          tourKey="users-form-tour"
          autoStart={true}
          showButton={true}
        />
      )}
      
      <Container title={id ? t('user:users.form.editTitle') : t('user:users.form.addTitle')} breadcrumb={t('user:users.breadcrumb')} linkPage="/users">
      {isLoading && <PageLoaderForm />}
      {showForm() && (
        <div className="card-content">
          <Row>
            <TitleForm>{t('user:users.form.dataSection')}</TitleForm>
          </Row>
          {!finished && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Row justifyContent="center">
                <Column xs={12} md={6} className="d-flex">
                  <div data-tour="user-name" style={{ width: '100%' }}>
                    <CustomTextField
                      {...register('name', { required: t('user:users.form.validation.nameRequired') })}
                      label={t('user:users.form.name')}
                      autoFocus
                      error={errors?.name ? true : false}
                      sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                      helperText={errors?.name ? errors.name.message : null}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                </Column>
                <Column xs={12} md={6} className="d-flex">
                  <div data-tour="user-email" style={{ width: '100%' }}>
                    <CustomTextField
                      {...register('email', { required: t('user:users.form.validation.emailRequired') })}
                      label={t('user:users.form.email')}
                      error={errors?.email ? true : false}
                      sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                      helperText={errors?.email ? errors.email.message : null}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                </Column>
                <Column xs={12} md={4} className="d-flex">
                  <div data-tour="user-phone" style={{ width: '100%', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    {allConfigs.length > 0 && (
                      <FormControl sx={{ flexShrink: 0, marginBottom: '24px' }}>
                        <Select
                          value={effectivePhoneCountry || ''}
                          onChange={(e) => setPhoneCountry(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: '90px' }}
                          renderValue={(val) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {val && <img src={`https://flagcdn.com/20x15/${val.toLowerCase()}.png`} alt={val} width={20} height={15} />}
                              <span style={{ fontSize: '0.8rem' }}>{phonePrefix}</span>
                            </Box>
                          )}
                        >
                          {allConfigs.map((c) => (
                            <MenuItem key={c.countryCode} value={c.countryCode}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <img src={`https://flagcdn.com/20x15/${c.countryCode.toLowerCase()}.png`} alt={c.countryCode} width={20} height={15} />
                                {c.name.pt} ({c.phonePrefix})
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <CustomTextField
                      {...register('cellphone')}
                      label={t('user:users.form.phone')}
                      sx={{ minHeight: '48px', margin: '0 auto 24px', flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                </Column>
                <Column xs={12} md={4} className="d-flex">
                  <div data-tour="user-groups" style={{ width: '100%' }}>
                    <Controller
                      control={control}
                      name="groups"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                        <InputLabel id="user-group-label" shrink>
                          {t('user:users.form.group')}
                        </InputLabel>
                        <Select
                          labelId="user-group-label"
                          id="groups"
                          multiple
                          notched
                          value={getSelectedContentByCompareFields(userGroups, value, 'name', 'companyKey')}
                          onChange={onChange}
                          input={<OutlinedInput id="user-group-chip" label={t('user:users.form.group')} />}
                          renderValue={(selected) => (
                            <Box
                              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                            >
                              {selected?.map((selectedValue) => (
                                <Chip
                                  key={`usergroup-chip-${selectedValue._id}`}
                                  label={selectedValue.name}
                                  variant="outlined"
                                  color="primary"
                                  onMouseDown={(e) => e.stopPropagation()} // 👈 impede o clique no "X" abrir o menu
                                  onDelete={(e) => {
                                    e.stopPropagation(); // 👈 impede propagação pro Select
                                    const newValue = value.filter((item) => item._id !== selectedValue._id);
                                    onChange(newValue); // 👈 remove o item da lista
                                  }}
                                  sx={{
                                    width: 'fit-content',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {userGroups?.map((item) => (
                            <MenuItem
                              key={item._id}
                              value={item}
                              style={getStyles(item._id, value._id, theme)}
                            >
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />                  </div>
                </Column>
                <Column xs={12} md={4} className="d-flex">
                  <div data-tour="user-permissions" style={{ width: '100%' }}>
                    <Controller
                      control={control}
                      name="permissions"
                      rules={{
                        required: t('user:users.form.validation.permissionRequired'),
                      }}
                    render={({ field: { onChange, value } }) => {
                      // Extrai o roleKey do primeiro item do array de permissões
                      const currentRoleKey = Array.isArray(value) && value.length > 0 
                        ? value[0]?.roleKey 
                        : value?.roleKey || '';
                      
                      return (
                        <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                          <InputLabel id="user-permission-label" shrink>
                            {t('user:users.form.permission')}
                          </InputLabel>
                          <Select
                            displayEmpty
                            labelId="user-permission-label"
                            id="permissions"
                            notched
                            label={t('user:users.form.permission')}
                            value={currentRoleKey}
                            onChange={(e) => {
                              const selectedRoleKey = e.target.value;
                              if (selectedRoleKey === '') {
                                onChange('');
                              } else {
                                const selectedRole = companyRoles?.find(role => role.roleKey === selectedRoleKey);
                                onChange(selectedRole);
                              }
                            }}
                            size="small"
                            style={{ marginBottom: '16px !important' }}
                            sx={{
                              '& > .MuiSelect-select.MuiSelect-outlined': {
                                paddingLeft: '0 !important',
                              },
                            }}
                          >
                            <MenuItem value={''} key={-1}>
                              {t('user:users.form.selectPermission')}
                            </MenuItem>
                            {companyRoles?.map((item) => (
                              <MenuItem
                                key={item.roleKey}
                                value={item.roleKey}
                              >
                                {item.roleName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  />                  </div>                </Column>

              </Row>
            <Row justifyContent="end" alignItems="center">
              <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
                <div data-tour="form-actions" style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
                  <ButtonForm secondary id="cancel" label={t('common:buttons.cancel')} onClick={() => navigate('/users')}></ButtonForm>
                  <ButtonForm label={t('common:buttons.save')} type="submit" />
                </div>
              </Column>
            </Row>
            </form>
          )}
          {finished && (
            <FormSuccess
              title={t('user:users.form.successTitle')}
              labelStartAgain={t('user:users.form.addAnother')}
              handleStartAgain={() => handleReset()}
              labelShow={t('user:users.form.viewDetails')}
              handleShow={() => navigate('/users/show/' + idUser)}
            />
          )}
        </div>
      )}
    </Container>
    </>
  );
};
